import React, { useState, useMemo, useEffect } from "react";
import { Box, Divider, Grid, Typography, Hidden } from "@mui/material";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { push } from "connected-react-router";
import { buildPolicyDetailsVersionsRoute } from "../../../../routes/AdminRoutes";
import PolicyOutline from "../../../../components/CourseOutline/PolicyOutline";
import {
  createRelease,
  validateRelease,
  deleteRelease,
  updateCourseVersionData,
  fetchCourseVersionData
} from "../../../../redux/actions/contentCoursesActions";
import PolicyForm from "../../../../components/Forms/PolicyForm";
import PolicyBuilder from "../../../../components/PolicyBuilder/PolicyBuilder";
import WarningModal from "../../../../components/WarningModal/WarningModal";
import { buildCoursePreviewRoute } from "../../../../routes/Routes";
import DeploymentModals from "./DeploymentModals";
import { useCourseSections } from "../../../../hooks/courses";
import { formatDate } from "../../../../utils/datetime";
import usePolicies from "../../../../hooks/policies";
import useUser from "../../../../hooks/useUser";
import PolicyVersionDetailsButtonsHeader from "./PolicyVersionDetailsButtonsHeader";
import PolicyVersionDetailsButtonsFooter from "./PolicyVersionDetailsButtonsFooter";
import { some } from "lodash";
import { useSnackbarNotifications } from "../../../../hooks/snackbar";

const firstReleaseVersion = "1.0";

const parseResponseErrors = errors => {
  if (
    some(
      errors,
      error => error.detail === "Number must be unique within manual"
    )
  ) {
    return { number: "managePolicies.error.duplicateNumber" };
  }
  return false;
};

const PolicyVersionDetailsInfo = ({
  updatedAt,
  currentReleaseVersion,
  template,
  releasePending
}) => {
  const { t } = useTranslation();
  return (
    <Grid container spacing={3}>
      <Grid item xs={6}>
        <Typography variant="subtitle1" color="textSecondary">
          {t("courseVersion.details.lastUpdated")}
        </Typography>
      </Grid>
      <Grid item xs={6}>
        <Typography variant="subtitle1" id="policyVersionUpdatedAt">
          {formatDate(updatedAt, "MM/DD/YYYY h:mm a")}
        </Typography>
      </Grid>
      <Grid item xs={6}>
        <Typography variant="subtitle1" color="textSecondary">
          {t("courseVersion.details.release")}
        </Typography>
      </Grid>
      <Grid item xs={6}>
        <Typography variant="subtitle1" id="currentReleaseVersion">
          {releasePending
            ? t("courseVersion.details.pendingRelease", {
                releaseVersion: currentReleaseVersion
              })
            : currentReleaseVersion}
        </Typography>
      </Grid>
      <Grid item xs={6}>
        <Typography variant="subtitle1" color="textSecondary">
          {t("courseVersion.details.template")}
        </Typography>
      </Grid>
      <Grid item xs={6}>
        <Typography variant="subtitle1" id="versionTemplate">
          {template || "Grace Hill"}
        </Typography>
      </Grid>
    </Grid>
  );
};

PolicyVersionDetailsInfo.propTypes = {
  courseVersionType: PropTypes.string,
  template: PropTypes.string,
  currentReleaseVersion: PropTypes.string,
  releasePending: PropTypes.bool
};

const PolicyVersionDetails = ({
  versionData: {
    pending,
    type,
    trainingHours,
    trainingMinutes,
    description,
    name,
    status,
    language,
    release,
    courseContent,
    number,
    updatedAt,
    templateOwnerName,
    id: currentVersionId,
    versionCustomerId
  },
  match: {
    params: { versionId, policyId }
  },
  setVersionDataState
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [isDeploying, setIsDeploying] = useState(false);
  const [isProcessingRelease, setIsProcessingRelease] = useState(false);
  const [warningMessage, setWarningMessage] = useState(null);
  const [formErrors, setFormErrors] = useState(false);
  const currentUser = useUser();

  const { isUpdatingCourseVersion } = useSelector(
    ({ contentCoursesState: { isUpdatingCourseVersion } }) => ({
      isUpdatingCourseVersion
    })
  );

  const { fetchCourseSections, mandatorySections } = useCourseSections(
    versionId
  );
  const { displayErrorMessage } = useSnackbarNotifications();
  const isFirstRelease = release === firstReleaseVersion;

  const { fetchPolicyVersion } = usePolicies();

  const handleSaveVersionDetails = (values, setSubmitting, setFieldError) => {
    return dispatch(
      updateCourseVersionData(
        policyId,
        versionId,
        values,
        () => {
          // success callback
          if (status === "Active" && values.status === "Inactive") {
            handleDetailsUpdate();
          } else {
            fetchCourseSections();
          }
          setSubmitting(false);
          setFormErrors(false);
        },
        errors => {
          // error callback
          const parsedErrors = parseResponseErrors(errors);
          if (parsedErrors.number) {
            setFieldError("number", t(parsedErrors.number));
          } else {
            errors[0]?.detail && displayErrorMessage(errors[0].detail);
          }
          setSubmitting(false);
          setFormErrors(true);
        }
      )
    );
  };

  useEffect(() => {
    const loadPolicyVersion = async () => {
      const data = await fetchPolicyVersion(policyId, versionId);
      setVersionDataState(data);
    };
    loadPolicyVersion();
  }, [isDeploying, isProcessingRelease]);

  const handleDetailsUpdate = () => {
    dispatch(push(buildPolicyDetailsVersionsRoute(policyId)));
  };

  const handleCreateReleaseClick = () => {
    setIsProcessingRelease(true);
    dispatch(createRelease(policyId, versionId)).then(() =>
      setIsProcessingRelease(false)
    );
  };

  const warningMessageText = affectedCourses => {
    if (!affectedCourses || affectedCourses.length === 0) {
      return t("courseVersion.details.pendingItemsMessageWarning", {
        insertion: ""
      });
    }
    return t("courseVersion.details.pendingItemsMessageWarning", {
      insertion: t("courseVersion.details.affectedCoursesMessageWarning", {
        affectedCourses: affectedCourses.join(", ")
      })
    });
  };

  const onDeleteRelease = () => {
    setIsProcessingRelease(true);
    dispatch(
      deleteRelease(policyId, versionId, () => {
        dispatch(fetchCourseVersionData(policyId, versionId)).then(() =>
          setIsProcessingRelease(false)
        );
        setWarningMessage(null);
      })
    ).then(() => fetchCourseSections());
  };

  const handleDeleteReleaseClick = () => {
    dispatch(
      validateRelease(policyId, versionId, validationResult => {
        if (validationResult.hasPendingActivities) {
          setWarningMessage({
            open: true,
            message: warningMessageText(validationResult.affectedCourses)
          });
        } else {
          onDeleteRelease();
        }
      })
    );
  };

  const handleDeployClick = () => {
    setIsDeploying(true);
  };

  const handlePreviewCourse = () => {
    window.open(buildCoursePreviewRoute(versionId));
  };

  const canCreateReleaseAndManageContent = () => {
    return versionCustomerId === currentUser.customerId;
  };

  const processingVideos = useMemo(() => {
    if (!courseContent) return [];
    const processing = courseContent.reduce(
      (processingItems, section) => [
        ...processingItems,
        ...section.items.filter(item => item.processing)
      ],
      []
    );
    return processing;
  }, [courseContent]);

  const initialVersionData = useMemo(
    () => ({
      name: name ? name : "",
      number: number ? number : "",
      description: description ? description : "",
      language: language ? language : "English",
      status: status,
      trainingHours: trainingHours ? trainingHours : 0,
      trainingMinutes: trainingMinutes ? trainingMinutes : 0
    }),
    [currentVersionId]
  );

  return (
    <Box display="flex" flexDirection="column">
      <PolicyVersionDetailsButtonsHeader
        onCreateReleaseClick={handleCreateReleaseClick}
        releasePending={pending}
        onPreviewClick={handlePreviewCourse}
        releaseDisabled={!canCreateReleaseAndManageContent()}
        disabled={isUpdatingCourseVersion || isDeploying || isProcessingRelease}
        isDeploying={isDeploying}
        processingVideos={processingVideos}
      />
      <Box display="flex" flexDirection={{ xs: "column", md: "row " }}>
        <Box display="flex" flexDirection="column" flex={5}>
          <PolicyForm
            initialValues={initialVersionData}
            onSubmit={(values, { setSubmitting, setFieldError }) => {
              handleSaveVersionDetails(values, setSubmitting, setFieldError);
            }}
            disabled={
              isUpdatingCourseVersion ||
              isDeploying ||
              !canCreateReleaseAndManageContent()
            }
            render={({
              formFields,
              formikProps: {
                isSubmitting,
                setSubmitting,
                setFieldError,
                values
              }
            }) => (
              <Box display="flex" flexDirection="column" pt={3}>
                <WarningModal
                  open={!!(warningMessage && warningMessage.open)}
                  onClose={() => setWarningMessage(null)}
                  onSubmit={() => onDeleteRelease()}
                  warningMessage={warningMessage ? warningMessage.message : ""}
                />
                <DeploymentModals
                  isDeploying={isDeploying}
                  newVersionData={values}
                  versionId={versionId}
                  courseId={policyId}
                  isPendingRelease={pending}
                  isFirstRelease={release === firstReleaseVersion}
                  onSaveVersionDetails={() =>
                    handleSaveVersionDetails(
                      values,
                      setSubmitting,
                      setFieldError
                    )
                  }
                  onDeployEnd={() => setIsDeploying(false)}
                  disableKickLearners={true}
                />
                <Box display="flex" flexDirection="column">
                  <Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        {formFields.number}
                      </Grid>
                      <Grid item xs={12}>
                        {formFields.name}
                      </Grid>
                      <Grid item xs={12}>
                        <Box my={2}>{formFields.description}</Box>
                      </Grid>
                      <Grid item xs={12}>
                        {formFields.language}
                      </Grid>
                      <Grid item xs={12}>
                        {formFields.status}
                      </Grid>
                      <Grid item xs={12} lg={6}>
                        {formFields.trainingHours}
                      </Grid>
                      <Grid item xs={12} lg={6}>
                        {formFields.trainingMinutes}
                      </Grid>
                    </Grid>
                  </Box>
                  <Box my={5}>
                    <PolicyVersionDetailsInfo
                      updatedAt={updatedAt}
                      currentReleaseVersion={release}
                      template={
                        versionCustomerId == 5
                          ? "Grace Hill"
                          : templateOwnerName
                      }
                      releasePending={pending}
                    />
                  </Box>
                </Box>
              </Box>
            )}
          />
        </Box>
        <Hidden smDown>
          <Divider
            orientation="vertical"
            sx={{ height: "unset", my: 0, mx: 4 }}
          />
        </Hidden>
        <Box flex={3} mt={1}>
          {pending ? (
            <PolicyBuilder
              policyId={policyId}
              versionId={versionId}
              disabled={isUpdatingCourseVersion || isDeploying}
              canManageContentForPolicy={canCreateReleaseAndManageContent()}
            />
          ) : (
            <PolicyOutline
              policyContent={mandatorySections}
              versionId={versionId}
            />
          )}
        </Box>
      </Box>
      <PolicyVersionDetailsButtonsFooter
        onDeployClick={handleDeployClick}
        onDeleteReleaseClick={handleDeleteReleaseClick}
        releasePending={pending}
        releaseFirst={isFirstRelease}
        releaseNumber={release}
        isReleaseOwner={canCreateReleaseAndManageContent()}
        deployDisabled={formErrors}
        disabled={
          isDeploying ||
          processingVideos.length ||
          isUpdatingCourseVersion ||
          !canCreateReleaseAndManageContent()
        }
      />
    </Box>
  );
};

export default PolicyVersionDetails;

PolicyVersionDetails.propTypes = {
  versionData: PropTypes.shape({
    type: PropTypes.string,
    recordScore: PropTypes.bool,
    release: PropTypes.string,
    pending: PropTypes.bool,
    language: PropTypes.string,
    trainingHours: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    trainingMinutes: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    status: PropTypes.string,
    description: PropTypes.string,
    name: PropTypes.string,
    versionCustomerId: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    courseContent: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        items: PropTypes.arrayOf(
          PropTypes.shape({
            type: PropTypes.string,
            title: PropTypes.string
          })
        )
      })
    ),
    resources: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        items: PropTypes.arrayOf(
          PropTypes.shape({
            type: PropTypes.string,
            title: PropTypes.string
          })
        )
      })
    )
  })
};
